/**
 * Stand Worksheet Print Pdf service
 */
import { Injectable, Injector } from '@angular/core';
import { get, filter, has, merge, isEmpty, includes } from 'lodash';
import { PdfBaseService } from '@app/shared/common/make-pdf';
import { PdfHeader } from '@app/shared/common/make-pdf/pdf-options.type';
import { TIME_FORMATS } from '@config/global-constants';
import { ENTRY_FORM_PDF_HEADERS } from '@shared/types/enums/stand-worksheet-pdf.enum';
import { ENTITY_TYPES, IBaseEntityType } from '@shared/types';
import { PermissionService } from '../session';
import { UiActions } from '@store/actions';
import { StandWorksheetEditContainerComponent } from '@app/test/shared/editors-containers';
import { buildSWSPdfConfig } from './stand-worksheet-pdf-config';
import { XuxEditGridComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import { lazyInject } from '@app/containers/base/decorators';
import {
  PdfGenerateServiceV2,
  PdfSelectColumn,
} from '@app/shared/common/make-pdf/v2';
import { CompanyUtilService } from '../company';
import {
  getObservableValue,
  truncationText,
} from '@app/shared/utils/common-utils';
import { STAND_WORKSHEET_STATUS_OPTIONS } from '@app/shared/types/enums/stand-worksheet-status.enum';
import { sortAndFilterData } from '@app/shared/common/make-pdf/v2/utils/pdf-data-sort-filter.utils';
import { getGridFilterStr } from '@app/shared/utils/grid-utils';
import {
  CHARGEABLE_LABEL,
  HIDE_COLUMNS_FIELDS_WHEN_OFF_POST_CLOSURE,
  STAND_WORKSHEET_PDF_SELECTED_COLUMNS,
} from './stand-worksheet.constants';
import { select } from '@angular-redux/store';
import { Observable } from 'rxjs';

/**
 * Stand Worksheet Print Pdf service that extends PdfBaseService to use
 * print empty stand worksheet
 */
@Injectable()
export class StandWorksheetPrintPdfService extends PdfBaseService {
  @select(['ui', STAND_WORKSHEET_PDF_SELECTED_COLUMNS])
  public printColumns$: Observable<PdfSelectColumn[]>;
  @lazyInject(PdfGenerateServiceV2)
  public pdfGenerateServiceV2: PdfGenerateServiceV2;
  @lazyInject(CompanyUtilService) public companyService: CompanyUtilService;

  public summaryTitleFontSize: number = 10;

  public constructor(
    public injector: Injector,
    public permissionService: PermissionService,
    public uiActions: UiActions,
  ) {
    super(injector);
  }

  public getPDFHeadersByPermission(): PdfHeader[] {
    let pdfHeadersByPermission: PdfHeader[] = filter(
      ENTRY_FORM_PDF_HEADERS(this.i18nService),
      (item) => {
        if (!has(item, 'permissionCode')) return true;
        if (
          !this.permissionService.canOperateByCode(
            ENTITY_TYPES.STAND_WORKSHEET,
            item.permissionCode,
          )
        ) {
          return false;
        }

        return true;
      },
    );
    // when DM post closure setting is off, should remove all fields about post_closure.
    pdfHeadersByPermission = this.removeRelatedFieldsColumnByPostClosureSetting(
      pdfHeadersByPermission,
    );

    return pdfHeadersByPermission;
  }

  public getPdfData(
    doc: IBaseEntityType,
    xuxGrid: XuxEditGridComponent,
  ): IBaseEntityType {
    const gridFilterString = getGridFilterStr(xuxGrid);

    const eventDateFormatter = (date: string): string =>
      this.companyService.convertUTCDateWithTimeZone(
        date,
        true,
        TIME_FORMATS.L_LT,
      );

    const virtualEvent = get(doc, 'virtual.event') ?? {};

    const originEventname = get(virtualEvent, 'name', get(doc, 'event_name'));

    const document: IBaseEntityType = {
      gridFilterString,
      originEventname,
      status: STAND_WORKSHEET_STATUS_OPTIONS[get(doc, 'status')],
      eventName: truncationText(originEventname, 25, this.summaryTitleFontSize),
      eventStartDate: eventDateFormatter(
        get(
          virtualEvent,
          'event_days[0].start_date_time',
          get(doc, 'event_start_date_time'),
        ),
      ),
      eventEndDate: eventDateFormatter(
        get(
          virtualEvent,
          'event_days[0].end_date_time',
          get(doc, 'event_end_date_time'),
        ),
      ),
      chargeableLabel: this.i18nService.instant(
        get(doc, 'viewSettingModalValue.chargeable', true)
          ? CHARGEABLE_LABEL.chargeable
          : CHARGEABLE_LABEL.allItems,
      ),
      fullSiteName: truncationText(
        `${get(doc, 'site_name', '')} [${get(doc, 'store_number', '')}]`,
        25,
        this.summaryTitleFontSize,
      ),
      siteName: get(doc, 'site_name', ''),
      informationList: get(doc, 'virtual.information_list', []),
      entryForm: sortAndFilterData(get(doc, 'items', []), {
        baseOnGrid: xuxGrid,
      }),
    };

    return document;
  }

  public removeRelatedFieldsColumnByPostClosureSetting(
    pdfHeader: PdfHeader[],
  ): PdfHeader[] {
    const isOpenPostClosureSetting =
      this.stateService.getStandWorksheetPostClosureSetting();

    if (isOpenPostClosureSetting) {
      return pdfHeader;
    }

    return filter(
      pdfHeader,
      (header) =>
        !includes(HIDE_COLUMNS_FIELDS_WHEN_OFF_POST_CLOSURE, header.dataKey),
    );
  }

  public async handlePdfClick(
    editor: StandWorksheetEditContainerComponent,
    action: 'print' | 'export',
  ): Promise<void> {
    const entryFormHeaders = this.getPDFHeadersByPermission();
    const isOffPostClosureSetting =
      !this.stateService.getStandWorksheetPostClosureSetting();
    const gridSearchStr = getGridFilterStr(editor.entityFormService.xuxGrid);
    const pdfConfig = buildSWSPdfConfig(
      async () =>
        this.getPdfData(
          merge(
            {
              viewSettingModalValue: get(
                editor,
                'entityFormService.viewSettingModalValue',
                {},
              ),
            },
            editor.form.value,
          ),
          editor.entityFormService.xuxGrid,
        ),
      {
        entryFormHeaders,
        isOffPostClosureSetting,
        uiActions: this.uiActions,
        pdfSelectColumn: getObservableValue(this.printColumns$),
        i18nService: this.i18nService,
      },
      !isEmpty(gridSearchStr),
    );

    const actionMapping = {
      print: this.pdfGenerateServiceV2.printPdfAsync.bind(
        this.pdfGenerateServiceV2,
      ),
      export: this.pdfGenerateServiceV2.downloadPdfAsync.bind(
        this.pdfGenerateServiceV2,
      ),
    };

    await actionMapping[action](pdfConfig);
  }
}
